import i18n from '@/utils/i18n';
import { PriceEditValidationError } from '@/domain/pricing/services/price-editor';

/**
 * Generates an i18n validation message for price editing violations.
 *
 * @param reason - The validation error type from the price editor.
 * @param bound - Optional formatted price string for floor/ceiling messages
 *   (e.g. "$1.50").
 * @returns A localized error message, or null if the reason is unrecognized.
 *
 * @example
 * ```ts
 * const msg = createPriceValidationMessage(
 *   reason, // PriceEditValidationError.EXCEEDS_FLOOR,
 *   '$1.50',
 * );
 * // => "Price cannot be below $1.50"
 * ```
 */
export function createPriceValidationMessage(
  reason: PriceEditValidationError,
  bound?: string,
): string | null {
  switch (reason) {
    case PriceEditValidationError.EXCEEDS_FLOOR:
      return i18n.t('sales_portal.order_entry.table.price_below_floor', {
        bound: bound ?? '',
      });
    case PriceEditValidationError.EXCEEDS_CEILING:
      return i18n.t('sales_portal.order_entry.table.price_above_ceiling', {
        bound: bound ?? '',
      });
    case PriceEditValidationError.GROSS_PROFIT_TOO_HIGH:
      return i18n.t('sales_portal.order_entry.table.gross_profit_too_high');
    default:
      return null;
  }
}

/**
 * Generates the compact banner copy for price editing violations
 * ("Below $1.50 floor" / "Above $1.50 ceiling").
 *
 * @param reason - The validation error type from the price editor.
 * @param bound - Optional formatted price string for floor/ceiling messages
 *   (e.g. "$1.50").
 * @returns A localized banner message, or null if the reason is unrecognized.
 *
 * @example
 * ```ts
 * const msg = createPriceValidationBannerMessage(
 *   reason, // PriceEditValidationError.EXCEEDS_FLOOR,
 *   '$1.50',
 * );
 * // => "Below $1.50 floor"
 * ```
 */
export function createPriceValidationBannerMessage(
  reason: PriceEditValidationError,
  bound?: string,
): string | null {
  switch (reason) {
    case PriceEditValidationError.EXCEEDS_FLOOR:
      return i18n.t(
        'sales_portal.order_entry.item_detail.pricing.below_floor',
        {
          bound: bound ?? '',
        },
      );
    case PriceEditValidationError.EXCEEDS_CEILING:
      return i18n.t(
        'sales_portal.order_entry.item_detail.pricing.above_ceiling',
        { bound: bound ?? '' },
      );
    case PriceEditValidationError.GROSS_PROFIT_TOO_HIGH:
      return i18n.t('sales_portal.order_entry.table.gross_profit_too_high');
    default:
      return null;
  }
}
